import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const VendorModal = ({
  open,
  onClose,
  onSave,
  vendorData = null,
  mode = 'create', // 'create', 'edit', 'view'
  balanceSheetHeads = [],
  existingVendors = [] // Add this prop to receive the list of existing vendors
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    BalanceSheetHeadId: '',
    Name: '',
    AddressLine1: '',
    AddressLine2: '',
    Location: '',
    PinCode: '',
    ContactNumber1: '',
    ContactNumber2: '',
    Email: '',
    GSTNumber: '',
    PanNumber: '',
    OpeningBalance: '',
    OpeningDate: new Date().toISOString().split('T')[0],
    IsPayableOrReceivable: false,
    Blacklist: false
  });

  const [contactPersons, setContactPersons] = useState([
    { ContactPerson: '', ContactNumber1: '', ContactNumber2: '' }
  ]);

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'vendorData:', vendorData);

      if (vendorData && mode !== 'create') {
        // Fetch vendor data from API for edit/view mode
        fetchVendorData(vendorData.Id);
      } else {
        console.log('Creating new vendor');
        // Reset form for create mode
        setFormData({
          BalanceSheetHeadId: '',
          Name: '',
          AddressLine1: '',
          AddressLine2: '',
          Location: '',
          PinCode: '',
          ContactNumber1: '',
          ContactNumber2: '',
          Email: '',
          GSTNumber: '',
          PanNumber: '',
          OpeningBalance: '',
          OpeningDate: new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: false,
          Blacklist: false
        });
        setContactPersons([{ ContactPerson: '', ContactNumber1: '', ContactNumber2: '' }]);
      }
      setErrors({});
    }
  }, [open, vendorData, mode]);

  const fetchVendorData = async (vendorId) => {
    setLoading(true);
    try {
      console.log('Fetching vendor data for ID:', vendorId);
      const response = await fetchData('VendorApi', `/Details/${vendorId}`, token);
      console.log('Vendor data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        const data = response.response_data;

        // Extract Vendor data
        const vendor = data.Vendor || data;

        console.log('Loading vendor data for edit/view, ID:', vendorId);
        setFormData({
          Id: vendor.Id || '',
          BalanceSheetHeadId: vendor.BalanceSheetHeadId || '',
          Name: vendor.Name || '',
          AddressLine1: vendor.AddressLine1 || '',
          AddressLine2: vendor.AddressLine2 || '',
          Location: vendor.Location || '',
          PinCode: vendor.PinCode || '',
          ContactNumber1: vendor.ContactNumber1 || '',
          ContactNumber2: vendor.ContactNumber2 || '',
          Email: vendor.Email || '',
          GSTNumber: vendor.GSTNumber || '',
          PanNumber: vendor.PanNumber || '',
          OpeningBalance: vendor.OpeningBalance || '',
          OpeningDate: vendor.OpeningDate ? vendor.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: vendor.IsPayableOrReceivable || false,
          Blacklist: vendor.Blacklist || false,
          IsActive: vendor.IsActive || false,
          CreatedBy: vendor.CreatedBy || 0,
          CreatedDate: vendor.CreatedDate ? vendor.CreatedDate.split('T')[0] : new Date().toISOString().split('T')[0],
        });

        // Load contact persons if available
        const contactDetails = data.VendorContactDetails || [];
        if (contactDetails.length > 0) {
          setContactPersons(contactDetails.map(contact => ({
            Id: contact.Id || 0,
            VendorId: contact.VendorId || 0,
            ContactPerson: contact.ContactPerson || '',
            ContactNumber1: contact.ContactNumber1 || '',
            ContactNumber2: contact.ContactNumber2 || '',
            IsActive: contact.IsActive || false,
            CreatedBy: contact.CreatedBy || 0,
            CreatedDate: contact.CreatedDate ? contact.CreatedDate.split('T')[0] : new Date().toISOString().split('T')[0],
          })));
        } else {
          setContactPersons([{ ContactPerson: '', ContactNumber1: '', ContactNumber2: '' }]);
        }
      } else {
        console.error('Failed to fetch vendor data or invalid response');
        alert('Error: Failed to load vendor data');
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      alert('Error: Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate contact numbers - only allow digits, spaces, +, -, and ()
    if ((name === 'ContactNumber1' || name === 'ContactNumber2') && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    // Validate PinCode - only allow digits
    if (name === 'PinCode' && value) {
      const pinCodePattern = /^[0-9]*$/;
      if (!pinCodePattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    // Validate PAN Number - only allow uppercase letters and digits
    if (name === 'PanNumber' && value) {
      const panPattern = /^[A-Z0-9]*$/;
      if (!panPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    // Validate GST Number format (15 characters: 2 digits + 10 alphanumeric + 1 digit + Z + 1 alphanumeric)
    if (name === 'GSTNumber' && value) {
      const gstPattern = /^[0-9A-Z]*$/;
      if (!gstPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleContactPersonChange = (index, field, value) => {
    // Validate contact numbers - only allow digits, spaces, +, -, and ()
    if ((field === 'ContactNumber1' || field === 'ContactNumber2') && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    const updatedContacts = [...contactPersons];
    updatedContacts[index][field] = value;
    setContactPersons(updatedContacts);

    // Clear contact person error when user starts typing
    if (errors.contactPersons) {
      setErrors(prev => ({
        ...prev,
        contactPersons: ''
      }));
    }
  };

  const addContactPerson = () => {
    setContactPersons([...contactPersons, { ContactPerson: '', ContactNumber1: '', ContactNumber2: '' }]);
  };

  const removeContactPerson = (index) => {
    if (contactPersons.length > 1) {
      const updatedContacts = contactPersons.filter((_, i) => i !== index);
      setContactPersons(updatedContacts);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = 'Vendor name is required';
    }

    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }

    if (!formData.ContactNumber1.trim()) {
      newErrors.ContactNumber1 = 'Contact number is required';
    } else {
      // Validate contact number format (should contain at least some digits)
      const digitsOnly = formData.ContactNumber1.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber1 = 'Contact number must have at least 10 digits';
      }
    }

    // Validate ContactNumber2 if provided
    if (formData.ContactNumber2.trim()) {
      const digitsOnly = formData.ContactNumber2.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber2 = 'Contact number must have at least 10 digits';
      }
    }

    // Validate Postal Code if provided (should be between 4-10 digits)
    if (formData.PinCode.trim()) {
      if (formData.PinCode.length < 4) {
        newErrors.PinCode = 'Postal code must be at least 4 digits';
      } else if (formData.PinCode.length > 10) {
        newErrors.PinCode = 'Postal code cannot exceed 10 digits';
      }
    }

    // Validate PAN Number format if provided (10 characters: 5 letters + 4 digits + 1 letter)
    if (formData.PanNumber.trim()) {
      const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panPattern.test(formData.PanNumber)) {
        newErrors.PanNumber = 'Invalid PAN format. Must be 10 characters (e.g., ABCDE1234F)';
      }
    }

    // Validate Email format if provided
    if (formData.Email.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.Email)) {
        newErrors.Email = 'Invalid email format';
      }
    }

    // Validate GST Number format if provided (15 characters: 2 digits + 10 alphanumeric + 1 digit + Z + 1 alphanumeric)
    if (formData.GSTNumber.trim()) {
      const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstPattern.test(formData.GSTNumber)) {
        newErrors.GSTNumber = 'Invalid GST format. Must be 15 characters with Z at 14th position (e.g., 22AAAAA0000A1Z5)';
      }
    }

    // Validate that at least one contact person has some data filled
    const hasValidContactPerson = contactPersons.some(contact =>
      contact.ContactPerson?.trim() || contact.ContactNumber1?.trim() || contact.ContactNumber2?.trim()
    );

    if (!hasValidContactPerson) {
      newErrors.contactPersons = 'At least one contact person detail is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateVendor = () => {
    // Check if a vendor with the same name and contact number exists in the list
    const duplicate = existingVendors.find(vendor => {
      // Skip the current vendor in edit mode
      if (mode === 'edit' && vendorData && vendor.Id === vendorData.Id) {
        return false;
      }
      
      // Check if name and contact number match
      const nameMatch = vendor.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      const contactMatch = vendor.ContactNumber1?.replace(/[^0-9]/g, '') === formData.ContactNumber1.replace(/[^0-9]/g, '');
      
      return nameMatch && contactMatch;
    });
    
    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate vendor
    const duplicate = checkDuplicateVendor();
    if (duplicate) {
      setDuplicateMessage(`A vendor with the name "${formData.Name}" and contact number "${formData.ContactNumber1}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Vendor: {
          BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
          Name: formData.Name,
          AddressLine1: formData.AddressLine1,
          AddressLine2: formData.AddressLine2,
          Location: formData.Location,
          PinCode: formData.PinCode,
          ContactNumber1: formData.ContactNumber1,
          ContactNumber2: formData.ContactNumber2,
          Email: formData.Email,
          GSTNumber: formData.GSTNumber,
          PanNumber: formData.PanNumber,
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate,
          IsPayableOrReceivable: formData.IsPayableOrReceivable,
          Blacklist: formData.Blacklist,
          CompanyId: companyId,
          Id: formData.Id || 0,
        },
        VendorContactDetails: contactPersons.filter(cp => cp.ContactPerson || cp.ContactNumber1 || cp.ContactNumber2).map(cp => ({
          Id: cp.Id || 0,
          ContactPerson: cp.ContactPerson || null,
          ContactNumber1: cp.ContactNumber1 || null,
          ContactNumber2: cp.ContactNumber2 || null
        }))
      };

      if (mode === 'edit') {
        // Validate that we have the vendor ID
        if (!vendorData || !vendorData.Id) {
          console.error('Cannot update: Missing vendor ID');
          alert('Error: Cannot update vendor - missing ID');
          return;
        }
        dataToSend.Id = vendorData.Id;
        // For edit mode, add modification fields
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (vendorData.CreatedBy) dataToSend.CreatedBy = vendorData.CreatedBy;
        if (vendorData.CreatedDate) dataToSend.CreatedDate = vendorData.CreatedDate;

        console.log('Updating vendor with ID:', vendorData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /VendorApi/' + vendorData.Id);

        const response = await updateData('VendorApi', vendorData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();

        console.log('Creating new vendor');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /VendorApi');

        const response = await postData('VendorApi', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving vendor:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Vendor';
      case 'edit': return 'Edit Vendor';
      case 'view': return 'Vendor Details';
      default: return 'Vendor';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BusinessIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          className="vendor-modal-content"
          style={{
            padding: '8px 12px',
            paddingBottom: '4px',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <style>
            {`
              .vendor-modal-content::-webkit-scrollbar {
                width: 0px;
                display: none;
              }
              .vendor-modal-content {
                scrollbar-width: none;
                -ms-overflow-style: none;
              }
            `}
          </style>
          <Typography variant="subtitle2" style={{
            color: '#666',
            marginBottom: '6px',
            fontSize: '10px',
            lineHeight: '1.2'
          }}>
            {mode === 'create' ? 'Add a new vendor to your system' :
              mode === 'edit' ? 'Update vendor information' :
                'View vendor details'}
          </Typography>

          <Grid container spacing={1}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Basic Information
              </Typography>
            </Grid>

            {/* Vendor Name and Balance Sheet Head - Same Row */}
            <Grid item xs={12} sm={8}>
              <TextField
                name="Name"
                label="Vendor Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter vendor name"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="BalanceSheetHeadId"
                label="Balance Sheet Head *"
                value={formData.BalanceSheetHeadId}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.BalanceSheetHeadId}
                helperText={errors.BalanceSheetHeadId}
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              >
                <option value=""></option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id} value={head.Id}>{head.Name}</option>
                ))}
              </TextField>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Address Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AddressLine1"
                label="Address Line 1"
                value={formData.AddressLine1}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Street address"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AddressLine2"
                label="Address Line 2"
                value={formData.AddressLine2}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Apartment, suite, etc."
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="Location"
                label="Location"
                value={formData.Location}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Location"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PinCode"
                label="Postal Code"
                value={formData.PinCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.PinCode}
                helperText={errors.PinCode}
                disabled={isReadOnly}
                placeholder="Postal Code"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Contact Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ContactNumber1"
                label="Contact Number 1 *"
                value={formData.ContactNumber1}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.ContactNumber1}
                helperText={errors.ContactNumber1}
                disabled={isReadOnly}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ContactNumber2"
                label="Contact Number 2"
                value={formData.ContactNumber2}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.ContactNumber2}
                helperText={errors.ContactNumber2}
                disabled={isReadOnly}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="Email"
                label="Email Address *"
                type="email"
                value={formData.Email}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Email}
                helperText={errors.Email}
                disabled={isReadOnly}
                placeholder="vendor@example.com"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Contact Person Details - Table Structure */}
            <Grid item xs={12}>
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '2px',
                marginBottom: '6px'
              }}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Contact Person Details
                </Typography>
                {!isReadOnly && (
                  <Button
                    onClick={addContactPerson}
                    size="small"
                    startIcon={<AddIcon style={{ fontSize: '12px' }} />}
                    style={{
                      background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                      color: 'white',
                      padding: '3px 8px',
                      fontSize: '10px',
                      textTransform: 'none',
                      minWidth: 'auto'
                    }}
                  >
                    Add
                  </Button>
                )}
              </Box>

              {errors.contactPersons && (
                <Typography style={{
                  color: '#d32f2f',
                  fontSize: '10px',
                  marginBottom: '4px',
                  marginLeft: '4px'
                }}>
                  {errors.contactPersons}
                </Typography>
              )}

              <Box
                className="contact-person-table-container"
                style={{
                  border: errors.contactPersons ? '1px solid #d32f2f' : '1px solid #e0e0e0',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}
              >
                <style>
                  {`
                    .contact-person-table-container::-webkit-scrollbar {
                      width: 0px;
                      display: none;
                    }
                    .contact-person-table-container {
                      scrollbar-width: none;
                      -ms-overflow-style: none;
                    }
                  `}
                </style>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '11px'
                }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{
                      background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                      color: 'white'
                    }}>
                      <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '11px',
                        width: '30%'
                      }}>Contact Person</th>
                      <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '11px',
                        width: '30%'
                      }}>Contact Number 1</th>
                      <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '11px',
                        width: '30%'
                      }}>Contact Number 2</th>
                      {!isReadOnly && (
                        <th style={{
                          padding: '8px',
                          textAlign: 'center',
                          fontWeight: '600',
                          fontSize: '11px',
                          width: '10%'
                        }}>Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {contactPersons.map((contact, index) => (
                      <tr key={index} style={{
                        borderBottom: index < contactPersons.length - 1 ? '1px solid #e0e0e0' : 'none',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                      }}>
                        <td style={{ padding: '6px' }}>
                          <TextField
                            value={contact.ContactPerson}
                            onChange={(e) => handleContactPersonChange(index, 'ContactPerson', e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isReadOnly}
                            placeholder="Name"
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            InputProps={{
                              style: {
                                fontSize: '11px',
                                height: '28px',
                                backgroundColor: 'white'
                              }
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px' }}>
                          <TextField
                            value={contact.ContactNumber1}
                            onChange={(e) => handleContactPersonChange(index, 'ContactNumber1', e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isReadOnly}
                            placeholder="Number 1"
                            variant="outlined"
                            inputProps={{ maxLength: 15 }}
                            InputProps={{
                              style: {
                                fontSize: '11px',
                                height: '28px',
                                backgroundColor: 'white'
                              }
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px' }}>
                          <TextField
                            value={contact.ContactNumber2}
                            onChange={(e) => handleContactPersonChange(index, 'ContactNumber2', e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isReadOnly}
                            placeholder="Number 2"
                            variant="outlined"
                            inputProps={{ maxLength: 15 }}
                            InputProps={{
                              style: {
                                fontSize: '11px',
                                height: '28px',
                                backgroundColor: 'white'
                              }
                            }}
                          />
                        </td>
                        {!isReadOnly && (
                          <td style={{ padding: '6px', textAlign: 'center' }}>
                            <IconButton
                              onClick={() => removeContactPerson(index)}
                              size="small"
                              disabled={contactPersons.length === 1}
                              style={{
                                color: contactPersons.length === 1 ? '#ccc' : '#ef4444',
                                padding: '2px'
                              }}
                            >
                              <DeleteIcon style={{ fontSize: '16px' }} />
                            </IconButton>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Grid>

            {/* Financial & Other Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{
                fontWeight: '600',
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Financial & Other Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="GSTNumber"
                label="GST Number"
                value={formData.GSTNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.GSTNumber}
                helperText={errors.GSTNumber}
                disabled={isReadOnly}
                placeholder="22AAAAA0000A1Z5"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PanNumber"
                label="PAN Number"
                value={formData.PanNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.PanNumber}
                helperText={errors.PanNumber}
                disabled={isReadOnly}
                placeholder="ABCDE1234F"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="OpeningBalance"
                label="Opening Balance"
                type="number"
                value={formData.OpeningBalance}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="0.00"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="OpeningDate"
                label="Opening Date"
                type="date"
                value={formData.OpeningDate}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                InputLabelProps={{
                  shrink: true,
                  style: labelStyle
                }}
                InputProps={{ style: inputStyle }}
              />
            </Grid>

            {/* Boolean Fields */}
            <Grid item xs={12}>
              <Box style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '4px' : '8px',
                marginTop: '2px'
              }}>
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                  minHeight: '28px'
                }}>
                  <input
                    type="checkbox"
                    name="IsPayableOrReceivable"
                    checked={formData.IsPayableOrReceivable}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    style={{
                      width: '12px',
                      height: '12px',
                      accentColor: "#0d766e"
                    }}
                  />
                  <Typography style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}>
                    Is Payable or Receivable
                  </Typography>
                </Box>

                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                  minHeight: '28px'
                }}>
                  <input
                    type="checkbox"
                    name="Blacklist"
                    checked={formData.Blacklist}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    style={{
                      width: '12px',
                      height: '12px',
                      accentColor: '#ff6b6b'
                    }}
                  />
                  <Typography style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}>
                    Blacklist
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          minHeight: 'auto'
        }}>
          <Button
            onClick={onClose}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '12px',
              padding: '4px 8px'
            }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '12px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Vendor' : 'Update Vendor'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Duplicate Warning Dialog */}
    <Dialog
      open={duplicateDialogOpen}
      onClose={() => setDuplicateDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon 
          style={{ 
            fontSize: '64px', 
            color: '#ff9800',
            marginBottom: '16px'
          }} 
        />
        <Typography 
          variant="h6" 
          style={{ 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}
        >
          Duplicate Vendor Found
        </Typography>
        <Typography 
          style={{ 
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {duplicateMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setDuplicateDialogOpen(false)}
          variant="contained"
          style={{
            background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '8px 32px',
            fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default VendorModal;